import React, { useEffect, useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * Collapsible FAQ section driven by /api/products/{id}/faqs.
 * Fetches language-aware Q&A (EN/ES/RU) and also injects the matching
 * FAQPage JSON-LD for Google rich results.
 */
const ProductFAQ = ({ productId, currentLanguage, productName }) => {
  const [faqs, setFaqs] = useState([]);
  const [schema, setSchema] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API}/api/products/${productId}/faqs?lang=${currentLanguage || 'en'}`
        );
        if (!res.ok) throw new Error(`FAQ ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setFaqs(data.faqs || []);
        setSchema(data.schema || null);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setFaqs([]);
          setSchema(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [API, productId, currentLanguage]);

  if (loading || error || !faqs.length) return null;

  const heading =
    currentLanguage === 'es'
      ? 'Preguntas Frecuentes'
      : currentLanguage === 'ru'
      ? 'Часто задаваемые вопросы'
      : 'Frequently Asked Questions';

  const subtitle =
    currentLanguage === 'es'
      ? `Información esencial sobre ${productName} antes de realizar tu pedido de investigación.`
      : currentLanguage === 'ru'
      ? `Всё, что нужно знать о ${productName} перед заказом для исследований.`
      : `Everything you need to know about ${productName} before you place your research order.`;

  return (
    <div className="mt-8" data-testid="product-faq-section">
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
      <div className="glass-card rounded-3xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-xl flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            {heading}
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-5">{subtitle}</p>

        <div className="divide-y divide-gray-200/70">
          {faqs.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div key={idx} className="py-3" data-testid={`faq-item-${idx}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full flex items-start justify-between gap-4 text-left group"
                  aria-expanded={isOpen}
                  data-testid={`faq-question-${idx}`}
                >
                  <span className="font-semibold text-gray-900 group-hover:text-capsule-teal transition-colors">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 text-capsule-teal transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
                    isOpen ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p
                    className="text-gray-700 leading-relaxed text-sm md:text-base"
                    data-testid={`faq-answer-${idx}`}
                  >
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductFAQ;
